import {
  collection,
  getDocs,
  doc,
  query,
  where,
  getDoc,
  updateDoc,
  arrayUnion,
  setDoc,
  deleteDoc,
} from "firebase/firestore";
import { trackEvent, getDateFromDatabaseField } from "../utils/helper-methods";
import { db } from "../utils/init-firebase";
import {
  DECK_COLLECTION_NAME,
  DeckObject,
  ExamObject,
  EXAMS_COLLECTION_NAME,
  FirebaseDate,
  UserData,
} from "../../constants/GlobalTypesAndEnumsAndConstants";

export const getExamsForClassDeck = async (
  classDeckId: string,
  includePastExams: boolean = false
): Promise<ExamObject[]> => {
  const deckDocRef = doc(db, DECK_COLLECTION_NAME, classDeckId);
  const deckDoc = await getDoc(deckDocRef);
  const deckData = deckDoc.data() as DeckObject;
  if (!deckData) {
    return [];
  }
  const examsIds = deckData.ids_of_exams_related_to_this_deck;
  if (!examsIds) {
    return [];
  }

  const examPromises = examsIds.map((examId) => getDoc(doc(db, EXAMS_COLLECTION_NAME, examId)));

  const examDocs = await Promise.all(examPromises);
  const exams = examDocs.filter((doc) => doc.exists()).map((doc) => ({ id: doc.id, ...doc.data() })) as ExamObject[];

  if (includePastExams) {
    return exams;
  } else {
    return exams.filter((exam) => exam.date_of_exam && exam.date_of_exam > new Date());
  }
};

export const updateExam = async (examId: string, dataToUpdate: any): Promise<void> => {
  await setDoc(doc(db, `${EXAMS_COLLECTION_NAME}`, examId), dataToUpdate, {
    merge: true,
  });

  return;
};

export const deleteExam = async (examId: string): Promise<void> => {
  await deleteDoc(doc(db, `${EXAMS_COLLECTION_NAME}`, examId));
  return;
};

/**
 * Returns exams for the given user that count towards the "exams survived" badges.
 * Criteria:
 * - user_id equals the given userId
 * - date_of_exam is 1 or more days in the past (applied client-side)
 * - has_exam_been_counted_for_exams_survived_badges === false (applied client-side)
 */
export const getEligibleExamsForExamsSurvivedBadgesForUser = async (userId: string): Promise<ExamObject[]> => {
  const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);

  // Server-side filtering: user, not yet counted, and exam date at least 1 day in the past
  const examsQuery = query(
    collection(db, EXAMS_COLLECTION_NAME),
    where("user_id", "==", userId),
    where("has_exam_been_counted_for_exams_survived_badges", "==", false),
    where("date_of_exam", "<=", oneDayAgo)
  );

  const examDocs = await getDocs(examsQuery);
  const exams = examDocs.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as ExamObject[];
  return exams;
};

/**
 * Marks the given exam ids with has_exam_been_counted_for_exams_survived_badges = true.
 */
export const markGivenExamsAsCountedForExamSurvivalBadges = async (examIds: string[]): Promise<void> => {
  if (!examIds || examIds.length === 0) return;
  const updates: Promise<any>[] = [];
  for (const examId of examIds) {
    const ref = doc(db, EXAMS_COLLECTION_NAME, examId);
    updates.push(updateDoc(ref, { has_exam_been_counted_for_exams_survived_badges: true }));
  }
  await Promise.allSettled(updates);
};

export const addGeneratedDeckToExam = async (
  examId: string,
  generatedDeckIdsArray: string[],
  user: UserData
): Promise<void> => {
  const examDocRef = doc(db, EXAMS_COLLECTION_NAME, examId);
  await updateDoc(examDocRef, {
    ids_of_decks_related_to_this_exam: arrayUnion(...generatedDeckIdsArray),
  });

  for (const generatedDeckId of generatedDeckIdsArray) {
    const deckDocRef = doc(db, DECK_COLLECTION_NAME, generatedDeckId);
    await updateDoc(deckDocRef, { ids_of_exams_related_to_this_deck: arrayUnion(examId) });
  }
  const examDoc = await getDoc(examDocRef);
  const examData = examDoc.data() as ExamObject;
  const numberOfDecksThatHaveBeenAddedToThisExam = examData?.ids_of_decks_related_to_this_exam?.length;
  const daysUntilExam = getDaysUntilExam(examData?.date_of_exam, user);
  trackEvent(
    "new generation/deck added to existing exam",
    {
      examId: examId,
      deck_ids_of_generated_decks: generatedDeckIdsArray,
      number_of_decks_that_have_been_added_to_this_exam: numberOfDecksThatHaveBeenAddedToThisExam,
      exam_name: examData?.exam_name,
      date_of_exam: examData?.date_of_exam,
      days_until_exam: daysUntilExam,
    },
    user
  );
};

/**
 * Calculate the number of days until an exam occurs, using the user's timezone.
 *
 * When an exam is created, the user sets the date of the exam by selecting a specific day.
 * By default we set the time of the exam to be at midnight of the selected day (the first
 * minute of the day in the user's timezone). Because of that we use Math.ceil when
 * calculating the number of days until the exam so that any partial remaining day counts
 * as a full "day until exam".
 */
export function getDaysUntilExam(examDate: Date | FirebaseDate | undefined, user?: UserData | null): number {
  if (!examDate) {
    return 0;
  }

  const userTimezone = user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

  // Get current time and localized exam date in the user's timezone
  const examDateFromDatabase = getDateFromDatabaseField(examDate);

  const currentTimeInUsersTimezone = new Date(new Date().toLocaleString("en-US", { timeZone: userTimezone }));
  const examDateInUsersTimezone = new Date(examDateFromDatabase.toLocaleString("en-US", { timeZone: userTimezone }));

  const timeUntilExamInSeconds = (examDateInUsersTimezone.getTime() - currentTimeInUsersTimezone.getTime()) / 1000;

  const daysUntilDateInUsersTimezone = Math.ceil(timeUntilExamInSeconds / (24 * 60 * 60));

  return daysUntilDateInUsersTimezone;
}
